import { savePicksToDatabase } from "db/info/pickemInfo";
import { TournamentSegment } from "model/Tournament/Segment/TournamentSegment";
import { pickemPath } from "pages/PageConfigs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { selectPickem } from "selectors/Pickem";
import { CenterFlexColumn } from "styles/layout";
import { EmptyGrowPanelText, ErrorText } from "styles/text";
import { SubmitPicksButton } from "./styles";
import { selectMatches, selectMatchesRosterIds } from "selectors/Match";
import { onlyUnique } from "Utility";
import { selectPickemMatchIdsFromSegment } from "selectors/PickemDetails";
import { PickemDetails } from "model/PickemDetails/PickemDetails";
import { Pickem, Pick } from "model/Pickem/Pickem";
import { DriverPicksContainer, DriverSelect } from "./Matchem/styles";
import { isMatchemDetails } from "model/PickemDetails/Matchem/Details";
import { MatchemPicker } from "./Matchem/MatchemPicker";
import { isRankemDetails } from "model/PickemDetails/Rankem/Details";
import { RankemPicker } from "./Rankem/RankemPicker";
import { isBracketemDetails } from "model/PickemDetails/Bracketem/Details";
import { BracketemPicker } from "./Bracketem/BracketemPicker";
import { getPointsPartitionTotalRangeSize } from "model/PickemDetails/PointsPartition";
import { Panel } from "components/Panel/Panel";
import { Match, getMatchDriverIds } from "model/Match/Match";
import { selectTournamentDriverNicknamesById } from "selectors/Roster";
import { useAppSelector } from "app/hooks";

interface Props {
  pickemDetails: PickemDetails;
  tournamentSegment: TournamentSegment;
}

const getInitialPicks = (
  ids: string[],
  pickem: Pickem | undefined,
  frozenTeams: { [id: string]: Pick } | undefined
) => {
  const initialPicks: { [id: string]: Pick } = {};
  if (!!pickem) {
    ids.forEach((id) => {
      initialPicks[id] = pickem.picks[id];
    });
  } else {
    ids.forEach((id) => {
      initialPicks[id] = undefined;
    });
  }

  if (!!frozenTeams) {
    const teamIds = Object.keys(frozenTeams);
    teamIds.forEach((teamId) => {
      initialPicks[teamId] = frozenTeams[teamId];
    });
  }
  return initialPicks;
};

const getInitialDriverPicks = (
  driverIds: string[],
  numToPick: number,
  pickem: Pickem | undefined
) => {
  if (numToPick <= 0) {
    return undefined;
  }

  const initialDriverPicks = Array<string | undefined>(numToPick).fill(
    undefined
  );
  if (!!pickem && !!pickem.driverPicks) {
    for (var i = 0; i < Math.min(pickem.driverPicks.length, numToPick); i++) {
      const initialPick = pickem.driverPicks[i];
      if (
        !initialDriverPicks.includes(initialPick) &&
        driverIds.includes(initialPick)
      ) {
        initialDriverPicks[i] = pickem.driverPicks[i];
      }
    }
  }

  return initialDriverPicks;
};

const checkPicksValid = (
  picks: { [id: string]: Pick },
  driverPicks: (string | undefined)[] | undefined,
  details: PickemDetails,
  segment: TournamentSegment,
  numberOfMatches: number
) => {
  if (!!driverPicks && driverPicks.some((d) => !d)) {
    return false;
  }

  if (isRankemDetails(details)) {
    var amountToPick = getPointsPartitionTotalRangeSize(
      details.pointsRules.columns
    );
    const numGroups = !!details.groupId ? 1 : segment.groups.length;
    if (segment.type === "Bracket" || segment.type === "Swiss") {
      amountToPick = amountToPick * numGroups;
    } else {
      amountToPick = amountToPick * numberOfMatches * numGroups;
    }
    return (
      Object.values(picks).filter((p) => p !== undefined).length ===
      amountToPick
    );
  }

  return !Object.values(picks).some((p) => p === undefined);
};

export const PickemPicker = ({ pickemDetails, tournamentSegment }: Props) => {
  const { uid } = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const pickem = useAppSelector((state) =>
    selectPickem(state, pickemDetails.tournamentId, pickemDetails.pickemId)
  );

  const matchIds = useAppSelector((state) =>
    selectPickemMatchIdsFromSegment(state, tournamentSegment, pickemDetails)
  );

  const rosterIds = useAppSelector((state) =>
    selectMatchesRosterIds(state, matchIds)
  );

  const pickingIds = isRankemDetails(pickemDetails)
    ? rosterIds.concat(pickemDetails.additionalRosterIds ?? [])
    : matchIds;

  const matches = useAppSelector((state) => selectMatches(state, matchIds));

  const driverIds = matches
    .flatMap((m) => getMatchDriverIds(m))
    .filter(
      (id) =>
        !pickemDetails.ignoreDrivers ||
        !pickemDetails.ignoreDrivers.includes(id)
    )
    .filter(onlyUnique);

  const driverNames = useAppSelector((state) =>
    selectTournamentDriverNicknamesById(
      state,
      driverIds,
      pickemDetails.tournamentId
    )
  );

  const sortedDriverIds = driverIds.sort((a, b) => {
    const aName = driverNames.get(a);
    const bName = driverNames.get(b);
    if (!aName) {
      return 1;
    }

    if (!bName) {
      return -1;
    }

    return aName.localeCompare(bName);
  });

  const [picks, setPicks] = useState(
    getInitialPicks(pickingIds, pickem, pickemDetails.frozenRosters)
  );
  const [driverPicks, setDriverPicks] = useState(
    getInitialDriverPicks(driverIds, pickemDetails.numDriverPicks, pickem)
  );
  const [errorText, setErrorText] = useState("");

  useEffect(() => {
    setPicks(getInitialPicks(pickingIds, pickem, pickemDetails.frozenRosters));
    setDriverPicks(
      getInitialDriverPicks(driverIds, pickemDetails.numDriverPicks, pickem)
    );
  }, [pickem]);

  const updateDriverPick = (
    driverPickIdx: number,
    newPick: string | undefined
  ) => {
    if (!driverPicks || driverPicks.length === 0) {
      return undefined;
    }

    const newDriverPicks = [...driverPicks];
    if (!!newPick) {
      for (var i = 0; i < newDriverPicks.length; i++) {
        if (newDriverPicks[i] === newPick) {
          newDriverPicks[i] = undefined;
        }
      }
    }

    newDriverPicks[driverPickIdx] = newPick;
    setDriverPicks(newDriverPicks);
  };

  const updatePicks = (newPicks: { id: string; pick: Pick }[]) => {
    const picksCopy = { ...picks };
    newPicks.forEach((newPick) => (picksCopy[newPick.id] = newPick.pick));
    setPicks(picksCopy);
  };

  const submitPicks = async () => {
    setErrorText("");
    const finalPicks = { ...picks };
    const finalDriverPicks = !!driverPicks ? [...driverPicks] : undefined;
    if (
      checkPicksValid(
        finalPicks,
        finalDriverPicks,
        pickemDetails,
        tournamentSegment,
        matches.length
      )
    ) {
      savePicksToDatabase(
        uid,
        pickemDetails.tournamentId,
        pickemDetails.pickemId,
        finalPicks,
        finalDriverPicks
      )
        .then(() => {
          navigate(pickemPath);
        })
        .catch((error) => {
          console.log(error);
          setErrorText(
            "Unable to submit pickem predictions. It may not be active."
          );
          return;
        });
    }
  };

  if (matches.length === 0) {
    return (
      <EmptyGrowPanelText>
        <ErrorText>Unable to build pick'em</ErrorText>
      </EmptyGrowPanelText>
    );
  }

  return (
    <>
      {getPickers(
        pickemDetails,
        tournamentSegment,
        matchIds,
        matches,
        picks,
        updatePicks
      )}
      {!!driverPicks && driverPicks.length > 0 && (
        <Panel padding="padded">
          <DriverPicksContainer>
            {driverPicks.map((driverPick, driverPickIdx) => (
              <DriverSelect
                value={driverPick}
                onChange={(e) => {
                  updateDriverPick(driverPickIdx, e.target.value);
                }}
              >
                <option value="">Select Driver</option>
                {sortedDriverIds
                  .filter(
                    (id) => id === driverPick || !driverPicks.includes(id)
                  )
                  .map((id) => (
                    <option value={id}>{driverNames.get(id)}</option>
                  ))}
              </DriverSelect>
            ))}
          </DriverPicksContainer>
        </Panel>
      )}
      <CenterFlexColumn>
        <SubmitPicksButton
          onClick={submitPicks}
          disabled={
            !checkPicksValid(
              picks,
              driverPicks,
              pickemDetails,
              tournamentSegment,
              matches.length
            )
          }
        >
          Submit Picks
        </SubmitPicksButton>
        <ErrorText>{errorText}</ErrorText>
      </CenterFlexColumn>
    </>
  );
};

const getPickers = (
  pickemDetails: PickemDetails,
  segment: TournamentSegment,
  matchIds: string[],
  matches: Match[],
  picks: {
    [id: string]: Pick;
  },
  updatePicks: (newPicks: { id: string; pick: Pick }[]) => void
) => {
  if (isBracketemDetails(pickemDetails)) {
    if (segment.type === "Bracket") {
      return (
        <BracketemPicker
          segment={segment}
          tournamentId={pickemDetails.tournamentId}
          groupId={pickemDetails.groupId}
          picks={picks}
          updatePicks={updatePicks}
        />
      );
    }

    return (
      <ErrorText>{`Bracket'em doesn't support segment of type ${segment.type}`}</ErrorText>
    );
  }

  if (isMatchemDetails(pickemDetails)) {
    return (
      <MatchemPicker
        matchemDetails={pickemDetails}
        tournamentSegment={segment}
        matches={matches}
        picks={picks}
        updatePicks={updatePicks}
      />
    );
  }

  if (isRankemDetails(pickemDetails)) {
    return (
      <RankemPicker
        rankemDetails={pickemDetails}
        tournamentSegment={segment}
        matchIds={matchIds}
        picks={picks}
        updatePicks={updatePicks}
      />
    );
  }

  return (
    <EmptyGrowPanelText>
      <ErrorText>Unexpected Pickem Type</ErrorText>
    </EmptyGrowPanelText>
  );
};
